import React, { JSX } from "react";
import {
  Autocomplete,
  Box,
  IconButton,
  Table,
  TableHead,
  TextField,
  Typography
} from "@mui/material";
import { useSelector } from "react-redux";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import produce from "immer";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";

import { RootState } from "../../store";
import { rupeeDisplay } from "../../helpers/rupee";
import { ProductType } from "../../interfaces/ProductInterface";
import useAssessmentLabel from "../../hooks/useAssessmentLabel";
import { ServiceInterface } from "../../interfaces/ServiceInterface";

// @ts-expect-error - css
import styles from "./Assessment.module.css";
import { DentalService, OtherService } from "../../interfaces/AssessmentInterfaces";

export const filterActiveLabService = (
  services: Array<ServiceInterface>,
  existingServices: Array<DentalService> | Array<OtherService>
): Array<ServiceInterface> => {
  const existingIds = new Set(existingServices.map((s) => s.id));

  return services.filter(
    (service) =>
      service.active && service.category !== ProductType.labTest && !existingIds.has(service.id)
  );
};

const IndividualRow = React.memo(
  ({
    item,
    otherServices,
    handleRemoveService,
    index
  }: {
    otherServices: OtherService[];
    handleRemoveService: (i: OtherService[], removedId: number) => void;
    item: OtherService;
    index: number;
  }) => (
    <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
      <TableCell sx={{ width: "60px" }}>{index + 1}. </TableCell>
      <TableCell>{item.name}</TableCell>
      <TableCell align="right">{rupeeDisplay(Number(item.grossTotalPrice))}</TableCell>
      <TableCell align="right">
        <IconButton
          color="error"
          onClick={() => {
            const mutatedServices = produce(otherServices, (draft) => {
              draft.splice(index, 1);
            });
            handleRemoveService(mutatedServices, item.id);
          }}
          size="small"
        >
          <DeleteForeverIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  )
);

const OtherServicesSelector = ({
  otherServices,
  handleAddService,
  handleRemoveService
}: {
  otherServices: Array<OtherService>;
  handleAddService: (v: ServiceInterface) => void;
  handleRemoveService: (i: Array<OtherService>, removedId: number) => void;
}): JSX.Element => {
  const assessmentLabels = useAssessmentLabel();
  const services = useSelector((state: RootState) => state.services.collection) || [];
  const filteredServices = filterActiveLabService(services, otherServices);

  return (
    <Box className={styles.assessmentRow}>
      <Box className={styles.assessmentLabel} sx={{ display: "flex", alignItems: "center" }}>
        <Typography component="span" fontWeight="600">
          {assessmentLabels.otherServices}
        </Typography>
      </Box>

      <Box className={styles.assessmentField}>
        <>
          <Box display="flex" alignItems="center" width="100%">
            <Autocomplete
              fullWidth
              options={filteredServices}
              value={[]}
              getOptionLabel={(option) => option.name || ""}
              renderOption={(params, option) => (
                // eslint-disable-next-line react/jsx-props-no-spreading
                <li {...params} key={option.id}>
                  {option.name || ""}
                </li>
              )}
              onChange={(_, v) => {
                if (v) {
                  handleAddService(v);
                }
              }}
              renderInput={(params) => (
                <TextField
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...params}
                  label={assessmentLabels.otherServices}
                  slotProps={{
                    inputLabel: { shrink: true }
                  }}
                  placeholder="Search and select services"
                  variant="outlined"
                  margin="dense"
                />
              )}
            />
          </Box>

          {otherServices.length > 0 && (
            <Box mt="16px">
              <TableContainer>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "60px" }}>SN.</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otherServices?.map((item, i) => (
                      <IndividualRow
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${item.name}-${i}`}
                        item={item}
                        index={i}
                        otherServices={otherServices}
                        handleRemoveService={handleRemoveService}
                        handleAddService={handleAddService}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </>
      </Box>
    </Box>
  );
};

export default OtherServicesSelector;
